// Small shared pieces for the Borrow & Return screens. Money, the empty state
// and the payment-mode buttons are the counter's own (../outpatient/ui) — one
// formatter and one set of mode buttons across the pharmacy, not two.
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import type { BorrowIssue, BorrowStatus, SettlementType } from './types';

export const SETTLEMENT_LABEL: Record<SettlementType, string> = {
  item_return: 'Same item return',
  payment: 'Payment',
};

const STATUS_META: Record<BorrowStatus, { label: string; variant: 'neutral' | 'warn' | 'info' | 'success' | 'danger' }> = {
  issued: { label: 'Issued – pending', variant: 'warn' },
  partially_returned: { label: 'Partially returned', variant: 'info' },
  partially_paid: { label: 'Partially paid', variant: 'info' },
  returned: { label: 'Returned', variant: 'success' },
  paid: { label: 'Paid', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};

/**
 * The stored status, plus the two derived ones the spec lists: "Overdue" (an
 * open record past its expected date — computed by the server as is_overdue)
 * and "Closed" (fully returned or paid).
 */
export function BorrowStatusBadges({ issue }: { issue: Pick<BorrowIssue, 'status' | 'is_overdue'> }) {
  const meta = STATUS_META[issue.status];
  const closed = issue.status === 'returned' || issue.status === 'paid';
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Badge variant={meta.variant}>{meta.label}</Badge>
      {issue.is_overdue && <Badge variant="danger">Overdue</Badge>}
      {closed && <Badge variant="neutral">Closed</Badge>}
    </span>
  );
}

/** Today as YYYY-MM-DD in the browser's own timezone (toISOString would be UTC — wrong for most of an IST morning). */
export function localDate(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** YYYY-MM-DD plus n days (calendar arithmetic, no timezone involved). */
export function addDaysYmd(ymd: string, n: number): string {
  if (!ymd) return '';
  return new Date(Date.parse(`${ymd}T00:00:00Z`) + n * 864e5).toISOString().slice(0, 10);
}

/** A DATE column (sent as a timestamp) back to YYYY-MM-DD, for a date input's min/value. */
export function ymd(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A DATE column as dd/mm/yyyy. The server sends it as a timestamp; only the calendar date is meaningful. */
export function dateOnly(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Expiry reads as month/year on a strip — "12/2027". */
export function expiryLabel(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** Holds a value back until it has stopped changing — keeps search boxes from firing per keystroke. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function ErrorBanner({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return <div className="rounded-lg bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{message}</div>;
}
